import type {
  CaseCoverageDto,
  CaseCoverageStatus,
  CasePriority,
  CoverageCountsDto,
  CoverageGroupDto,
} from '@tms/shared';
import { Link } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  EntityKey,
  ErrorState,
  PriorityBadge,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Th,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useFormat, useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { usePermissions } from '../auth/permissions';
import { CauseBadge } from '../automation/ui';
import { useProjectCoverage, useRefreshCoverage } from './api';

const WINDOWS = [7, 30] as const;

/** The case's status: its bar segment and badge colour. */
const STATUS_STYLE: Record<CaseCoverageStatus, { bar: string; badge: string; text: string }> = {
  working: { bar: 'bg-pass', badge: 'bg-pass-soft text-pass', text: 'text-pass' },
  partial: { bar: 'bg-fail/45', badge: 'bg-fail-soft text-fail', text: 'text-fail' },
  red: { bar: 'bg-fail', badge: 'bg-fail-soft text-fail', text: 'text-fail' },
  // the accent, not a result colour: the code needs fixing, not the product
  gone: { bar: 'bg-accent', badge: 'bg-accent-soft text-accent-fg', text: 'text-accent-fg' },
  // amber, not grey: an autotest that only skips is a warning, and grey is «not run»
  skipped: { bar: 'bg-block', badge: 'bg-block-soft text-block', text: 'text-block' },
  noData: { bar: 'bg-notrun', badge: 'bg-notrun-soft text-notrun', text: 'text-notrun' },
  none: { bar: 'bg-sunken', badge: 'bg-sunken text-ink-muted', text: 'text-ink-muted' },
};
const ORDER: CaseCoverageStatus[] = [
  'working',
  'partial',
  'red',
  'gone',
  'skipped',
  'noData',
  'none',
];
const ATTENTION: CaseCoverageStatus[] = ['red', 'partial', 'gone', 'skipped', 'noData', 'none'];
/** Shown in the tables always; the rest only when some case has them. */
const ALWAYS: CaseCoverageStatus[] = ['working', 'red', 'skipped', 'noData'];
/** The list to look at: critical first — «critical, no autotest» is the automation backlog. */
const PRIORITY_RANK: Record<CasePriority, number> = { critical: 0, high: 1, medium: 2, low: 3 };

type CountedStatus = Exclude<CaseCoverageStatus, 'none'>;

const countOf = (counts: CoverageCountsDto, status: CaseCoverageStatus): number =>
  status === 'none' ? counts.cases - counts.linked : counts[status];

const percent = (part: number, whole: number): number =>
  whole === 0 ? 0 : Math.round((part / whole) * 100);

const CoverageBar = ({ counts }: { counts: CoverageCountsDto }) => {
  const { t } = useT();
  if (counts.cases === 0) return <div className="h-2 w-full rounded-full bg-sunken" />;
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-sunken">
      {ORDER.map((status) => (
        <div
          key={status}
          className={STATUS_STYLE[status].bar}
          style={{ width: `${(countOf(counts, status) / counts.cases) * 100}%` }}
          title={`${t(`coverage.status.${status}`)}: ${countOf(counts, status)}`}
        />
      ))}
    </div>
  );
};

const GroupTable = ({
  title,
  groups,
  columns,
  label,
}: {
  title: string;
  groups: CoverageGroupDto[];
  columns: CountedStatus[];
  label: (group: CoverageGroupDto) => ReactNode;
}) => {
  const { t } = useT();
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">{title}</h3>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>{t('coverage.col.group')}</Th>
              <Th className="text-right">{t('coverage.col.cases')}</Th>
              <Th className="text-right">{t('coverage.col.linked')}</Th>
              {columns.map((status) => (
                <Th key={status} className="text-right">
                  {t(`coverage.status.${status}`)}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.id}>
                <Td>{label(group)}</Td>
                <Td className="text-right tabular-nums">{group.cases}</Td>
                <Td className="text-right tabular-nums">{group.linked}</Td>
                {columns.map((status) => (
                  <Td
                    key={status}
                    className={cn(
                      'text-right tabular-nums',
                      group[status] > 0 ? STATUS_STYLE[status].text : 'text-ink-subtle',
                    )}
                  >
                    {group[status]}
                    {status === 'working' && group.cases > 0 ? (
                      <span className="ml-1 text-[11px] text-ink-subtle">
                        {percent(group.working, group.cases)}%
                      </span>
                    ) : null}
                  </Td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </div>
  );
};

const CaseRow = ({ projectCode, item }: { projectCode: string; item: CaseCoverageDto }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const result = item.result;
  const testName = result
    ? `${result.className}${result.testMethod ? `.${result.testMethod}` : ''}`
    : '';
  return (
    <li className="flex flex-col gap-1 px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={STATUS_STYLE[item.status].badge}>
          {t(`coverage.status.${item.status}`)}
        </Badge>
        <PriorityBadge priority={item.priority} />
        <Link
          to="/projects/$projectCode/test-cases/$caseKey"
          params={{ projectCode, caseKey: item.caseKey }}
          className="min-w-0 truncate text-[13px] font-medium text-ink hover:text-accent-fg"
        >
          <EntityKey>{item.caseKey}</EntityKey> {item.title}
        </Link>
      </div>
      {result?.gone ? (
        <p className={cn('text-[12px]', STATUS_STYLE.gone.text)}>
          {result.projectName} ·{' '}
          {t(result.gone === 'class' ? 'coverage.goneClass' : 'coverage.goneTest', {
            name: testName,
          })}
        </p>
      ) : result ? (
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-muted">
          <span>
            {result.projectName} · {testName}
          </span>
          {result.pipelineId ? (
            <Link
              to="/automation/runs/$runId"
              params={{ runId: result.pipelineId }}
              search={{ project: result.automationProjectId }}
              className="text-accent hover:underline"
            >
              #{result.pipelineId}
            </Link>
          ) : null}
          {result.at ? <span className="text-ink-subtle">{relative(result.at)}</span> : null}
          {/* a whole class: how much of it passed, so an unrelated red test is visible as one */}
          {!result.testMethod && result.counts && result.counts.total > 1 ? (
            <span className="tabular-nums">
              {t('coverage.passedOf', {
                passed: result.counts.passed,
                total: result.counts.total,
              })}
            </span>
          ) : null}
          {/* a known defect already says whose side it is; the cause by text would only argue with it */}
          {result.defectId ? (
            <Badge className="bg-sunken text-ink-muted">
              {/* D-… is a registered defect; other known reasons are of the run itself */}
              {t(/^D-\d+$/.test(result.defectId) ? 'coverage.defect' : 'coverage.knownCause', {
                id: result.defectId,
              })}
            </Badge>
          ) : (
            <CauseBadge cause={result.cause} />
          )}
        </div>
      ) : null}
      {result?.message ? (
        <p
          className={cn(
            'line-clamp-2 whitespace-pre-wrap break-words text-[12px]',
            STATUS_STYLE[item.status].text,
          )}
        >
          {result.message}
        </p>
      ) : null}
    </li>
  );
};

/**
 * Coverage that works: how many of the project's active cases have an autotest
 * that actually passed recently — not merely one that exists. Red, partly red,
 * broken links, skipped and not run autotests are listed for a look.
 */
export const CoverageCard = ({ projectCode }: { projectCode: string }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const { errorText } = useErrorText();
  const { can } = usePermissions();
  const { priority: priorityLabel } = useEnumLabels();
  const [days, setDays] = useState<number>(WINDOWS[0]);
  // null — the first kind that has cases, worst first
  const [picked, setFilter] = useState<CaseCoverageStatus | null>(null);
  const { data, isPending, error, refetch } = useProjectCoverage(projectCode, days);
  const refresh = useRefreshCoverage(projectCode);
  const filter: CaseCoverageStatus =
    picked ??
    (data ? ATTENTION.find((status) => countOf(data.totals, status) > 0) : undefined) ??
    'red';
  const columns = data
    ? (ORDER.filter(
        (status) =>
          status !== 'none' && (ALWAYS.includes(status) || countOf(data.totals, status) > 0),
      ) as CountedStatus[])
    : [];

  const onRefresh = () =>
    refresh.mutate(undefined, {
      onSuccess: ({ pipelines }) =>
        toast.success(
          pipelines > 0
            ? t('coverage.refreshed', { count: pipelines })
            : t('coverage.refreshedNone'),
        ),
      onError: (cause) => toast.error(errorText(cause)),
    });

  const actions = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {can('automation:run') ? (
        <Button size="sm" onClick={onRefresh} loading={refresh.isPending}>
          {refresh.isPending ? null : <RefreshCw className="size-3.5" />}
          {t('coverage.refresh')}
        </Button>
      ) : null}
      <div className="flex gap-1" role="group" aria-label={t('coverage.title')}>
        {WINDOWS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setDays(value)}
            aria-pressed={days === value}
            className={cn(
              'whitespace-nowrap rounded-md px-2 py-1 text-[12px] font-medium',
              days === value ? 'bg-accent-soft text-accent-fg' : 'text-ink-muted hover:bg-sunken',
            )}
          >
            {t('coverage.days', { days: value })}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader
        title={t('coverage.title')}
        description={t('coverage.description', { days })}
        action={actions}
      />
      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <SkeletonRows rows={3} />
      ) : data.totals.linked === 0 ? (
        <EmptyState title={t('coverage.noLinks')} description={t('coverage.noLinksHint')} />
      ) : (
        <div className="flex flex-col gap-4 px-4 py-3">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-[15px] font-semibold text-ink">
                {t('coverage.headline', { working: data.totals.working, cases: data.totals.cases })}
                <span className="ml-2 text-pass">
                  {percent(data.totals.working, data.totals.cases)}%
                </span>
              </p>
              <p className="text-[12px] text-ink-muted">
                {[
                  data.totals.linked - data.totals.working > 0
                    ? t('coverage.linkedNotWorking', {
                        count: data.totals.linked - data.totals.working,
                      })
                    : null,
                  data.totals.cases - data.totals.linked > 0
                    ? t('coverage.withoutAutotest', {
                        count: data.totals.cases - data.totals.linked,
                      })
                    : null,
                  data.collectedAt
                    ? t('coverage.collectedAt', { when: relative(data.collectedAt) })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <CoverageBar counts={data.totals} />
            <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[13px]">
              {ORDER.filter(
                (status) => ALWAYS.includes(status) || countOf(data.totals, status) > 0,
              ).map((status) => (
                <div
                  key={status}
                  className="flex gap-1.5"
                  title={t(`coverage.status.${status}.hint`)}
                >
                  <dt className="text-ink-muted">{t(`coverage.status.${status}`)}</dt>
                  <dd className={cn('font-medium tabular-nums', STATUS_STYLE[status].text)}>
                    {countOf(data.totals, status)}
                  </dd>
                </div>
              ))}
            </dl>
            {!data.collectedAt ? (
              <p className="text-[12px] text-ink-muted">{t('coverage.notCollected')}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-4">
            <GroupTable
              title={t('coverage.byPriority')}
              groups={data.byPriority}
              columns={columns}
              label={(group) => priorityLabel(group.id as CasePriority)}
            />
            {data.byFolder.length > 1 ? (
              <GroupTable
                title={t('coverage.byFolder')}
                groups={data.byFolder}
                columns={columns}
                label={(group) => (group.id === '—' ? t('coverage.noFolder') : group.label)}
              />
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
                {t('coverage.attention')}
              </h3>
              <div
                className="flex flex-wrap gap-1"
                role="group"
                aria-label={t('coverage.attention')}
              >
                {ATTENTION.filter(
                  (status) =>
                    status === 'none' ||
                    ALWAYS.includes(status) ||
                    countOf(data.totals, status) > 0,
                ).map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setFilter(status)}
                    aria-pressed={filter === status}
                    title={t(`coverage.status.${status}.hint`)}
                    className={cn(
                      'rounded-md px-2 py-1 text-[12px] font-medium',
                      filter === status
                        ? STATUS_STYLE[status].badge
                        : 'text-ink-muted hover:bg-sunken',
                    )}
                  >
                    {t(`coverage.status.${status}`)} · {countOf(data.totals, status)}
                  </button>
                ))}
              </div>
            </div>
            {data.cases.some((c) => c.status === filter) ? (
              <ul className="divide-y divide-border rounded-md border border-border">
                {data.cases
                  .filter((c) => c.status === filter)
                  .sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority])
                  .map((item) => (
                    <CaseRow key={item.caseKey} projectCode={projectCode} item={item} />
                  ))}
              </ul>
            ) : (
              <p className="text-[13px] text-ink-muted">{t('coverage.attentionEmpty')}</p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
